import get from 'lodash/get';
import isString from 'lodash/isString';
import isNull from 'lodash/isNull';
import isUndefined from 'lodash/isUndefined';
import mapValues from 'lodash/mapValues';
import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';

import { currencyFormatter, getMessage } from 'util/locale';
import {
  formatNumberGrouped,
  formatNumberRoundDownToNearest,
  formatNumberRoundUpToNearest
} from 'util/formatters/numberFormatter';
import {
  layerTypes,
  MIN_TARGET_YIELD,
  RATIOS,
  requiredNumImages,
  requiredNumSupportedCropImages
} from 'constants/advanced';
import {
  applicationProperties,
  applicationTypes,
  scriptTypes
} from 'constants/scripts';
import { substanceTypeIsNitrogen } from 'products/fertility-selectors';
import { determineProductValidationValueByCropId } from 'user/selectors';
import { getCurrency } from 'user/userSelectors';


import validate from 'validate.js'; // The .js is part of the package name
import { getActiveCropId } from './currentSelectors';

/** Convenience wrapper for calling validate/formValidate() function from validate.js external package. See
 *  https://validatejs.org/#validate-js for details, although we appear to be using a pretty old version of the
 *  package, and the current docs aren't fully applicable.
 *
 *  @param toTest Object with members containing name/value pairs to validate.
 *  @param rules  Object of rule definitions used for validation.
 *
 *  @return An object containing a key for every member found in toTest, and a value for that member that is null
 *          if the key value passed validation, or an array of error messages if it violated one or more rules.
 */

const validateWithOptions = (toTest, rules) => {
  const options = { fullMessages: false };

  const testKeys = mapValues(toTest, () => null);

  // merge with an object of blank test keys so that if any values come back as unassigned (valid), we
  // blank them out in the validations store
  return {...testKeys, ...validate(toTest, rules, options)};
};

// Make it a function so that getMessage won't be evaluated before locale is set properly
const getScriptNameRules = () => ({
  presence: { message: getMessage('help-text__rx-name-required') },
  format: {

    /*
      This format is meant to disallow invalid characters when naming a prescription. The requirements for this are
      documented in CFVWEB-8427, CFVWEB-8007, CFVWEB-5533, and CFVWEB-3444

      We should not accept any prescription name with these characters (note there are 3 spaces between each character
      for readability) (as of 4/1/2020):
        !   @   #   $   %   ^   &   *   (   )   [   ]   {   }   +   =   .   <   >   ?   '   ;   :   |   `   ~   /   \

      On regex101.com, use ECMAScript(JavaScript) to evaluate and debug this pattern:
      /[^!@#\$%\^\&*\)\(+=.<>?';:|{}`~\[\]/\\]+$/
    */

    pattern: /[^!@#\$%\^\&*\)\(+=.<>?';:|{}`~\[\]/\\]+$/,
    message: getMessage('help-text__rx-name-alphanum')

  }
});

export const validateScriptName = ({ scriptName }) => {
  // validate empty string script name as being missing so
  // that the 'required' validation will show instead of the
  // format validation
  if (scriptName === '') {
    scriptName = null;
  }
  const toTest = {scriptName};

  const rules = {
    scriptName: getScriptNameRules()
  };

  return validateWithOptions(toTest, rules);
};

export const validateSeedProductSelectorBrand = ({ brandUuid }) => {
  const toTest = {brandUuid};

  const rules = {
    brandUuid: {
      presence: {
        message: getMessage('help-text__value-required', { prop: getMessage('brand')})
      },
      length: {
        minimum: 1,
        message: getMessage('help-text__value-required', { prop: getMessage('brand')})
      }
    }
  };
  return validateWithOptions(toTest, rules);
};

export const validateSeedProductSelectorRmValues = ({ standardRm, rmMax, rmMin }) => {
  const minOfRmMax = rmMin < standardRm.min || rmMin > standardRm.max ? standardRm.min : Number(rmMin);
  const maxOfRmMin = rmMax < standardRm.min || rmMax > standardRm.max ? standardRm.max : Number(rmMax);

  const rules = {
    rmMax: {
      presence: {
        message: getMessage('help-text__value-required')
      },
      numericality: {
        strict: true,
        notValid: getMessage('help-text__value-num'),

        greaterThanOrEqualTo: minOfRmMax,
        notGreaterThanOrEqualTo: getMessage('help-text__value-min', { min: formatNumberGrouped(minOfRmMax) }),

        lessThanOrEqualTo: standardRm.max,
        notLessThanOrEqualTo: getMessage('help-text__value-max', { max: formatNumberGrouped(standardRm.max) })
      }
    },
    rmMin: {
      presence: {
        message: getMessage('help-text__value-required')
      },
      numericality: {
        strict: true,
        notValid: getMessage('help-text__value-num'),

        greaterThanOrEqualTo: standardRm.min,
        notGreaterThanOrEqualTo: getMessage('help-text__value-min', { min: formatNumberGrouped(standardRm.min) }),

        lessThanOrEqualTo: maxOfRmMin,
        notLessThanOrEqualTo: getMessage('help-text__value-max', { max: formatNumberGrouped(maxOfRmMin) })
      }
    }
  };

  const toTest = { rmMax, rmMin };

  return validateWithOptions(toTest, rules);
};

export const validateIncludedCciLayersForRecommendations = ({cciLayers=[], supportedCropCciLayers=[]}) => {
  const toTest = {
    cciLayers,
    numCciLayers: cciLayers.length,
    numSupportedCropCciLayers: supportedCropCciLayers.length
  };

  const rules = {
    cciLayers: {
      presence: true
    },
    numCciLayers: {
      numericality: {
        greaterThanOrEqualTo: requiredNumImages[layerTypes.CCI]
      }
    },
    numSupportedCropCciLayers: {
      numericality: {
        greaterThanOrEqualTo: requiredNumSupportedCropImages[layerTypes.CCI]
      }
    }
  };

  return validateWithOptions(toTest, rules);
};

export const validateIncludedYieldLayersForRecommendations = ({yieldLayers=[], supportedCropYieldLayers=[]}) => {
  const toTest = {
    yieldLayers,
    numYieldLayers: yieldLayers.length,
    numSupportedCropYieldLayers: supportedCropYieldLayers.length
  };

  const rules = {
    yieldLayers: {
      presence: true
    },
    numYieldLayers: {
      numericality: {
        greaterThanOrEqualTo: requiredNumImages[layerTypes.YIELD]
      }
    },
    numSupportedCropYieldLayers: {
      numericality: {
        greaterThanOrEqualTo: requiredNumSupportedCropImages[layerTypes.YIELD]
      }
    }
  };

  return validateWithOptions(toTest, rules);
};

export const validatePlantingPlan = ({ toTest, constraints, currency, cropId }) => {
  const { max: priceCommodityMax, min: priceCommodityMin } = get(constraints, 'priceCommodity', {});
  const { max: priceProductMax, min: priceProductMin } = get(constraints, 'priceProduct', {});
  const { max: targetYieldMax, min: targetYieldMin } = get(constraints, 'targetYield', {});
  const { max: rateMax, min: rateMin } = get(constraints, 'currentAveragePopulation', {});

  // Get formatted currency values for any defined constraints.
  let priceCommodityMinDisplay, priceCommodityMaxDisplay, priceProductMinDisplay, priceProductMaxDisplay;
  if (constraints) {
    if (constraints.priceCommodity) {
      priceCommodityMinDisplay = currencyFormatter(priceCommodityMin, currency);
      priceCommodityMaxDisplay = currencyFormatter(priceCommodityMax, currency);
    }
    if (constraints.priceProduct) {
      priceProductMinDisplay = currencyFormatter(priceProductMin, currency);
      priceProductMaxDisplay = currencyFormatter(priceProductMax, currency);
    }
  }

  let rules = {
    // all manual
    activityType: { presence: true },
    activityUuid: { presence: true },
    date: { presence: true },
    defaultScriptName: {
      presence: { message: getMessage('help-text__rx-name-required') },
      format: {
        pattern: /^[\w\-]*$/,
        message: getMessage('help-text__rx-name-alphanum')
      }
    },
    products: {
      length: {
        minimum: 1,
        tooShort: getMessage('help-text__value-required', { prop: getMessage(`product.${cropId}`) })
      }
    },
    scriptName: getScriptNameRules(),

    // manual fertility
    activityCategoryUuid: { presence: true },
    hasNitrapyrin: { presence: true },
    incorporationMethod: {
      presence: {
        message: getMessage('help-text__value-required', { prop: getMessage('incorporation-method') })
      }
    },
    substanceTypeUuid: {
      presence: {
        message: getMessage('help-text__value-required', { prop: getMessage('product-type') })
      }
    },

    // manual planting
    hasPlantingFertilizerApplication:{
      presence: true
    },

    // advanced
    goalRatio: { presence: true },
    productId: {
      presence: {
        message: getMessage('help-text__value-required', { prop: getMessage(`product.${cropId}`) })
      },
    },
    priceCommodity: {
      presence: {
        message: getMessage('help-text__value-required', { prop: getMessage('price-commodity') })
      },
      numericality: {
        greaterThanOrEqualTo: priceCommodityMin,
        lessThanOrEqualTo: priceCommodityMax,
        notGreaterThanOrEqualTo: getMessage('help-text__value-min', {
          prop: getMessage('price-commodity'), min: priceCommodityMinDisplay
        }),
        notLessThanOrEqualTo: getMessage('help-text__value-max', {
          prop: getMessage('price-commodity'), max: priceCommodityMaxDisplay
        })
      }
    },
    priceProduct: {
      presence: {
        message: getMessage('help-text__value-required', { prop: getMessage('price-product') })
      },
      numericality: {
        greaterThanOrEqualTo: priceProductMin,
        lessThanOrEqualTo: priceProductMax,
        notGreaterThanOrEqualTo: getMessage('help-text__value-min', {
          prop: getMessage('price-product'), min: priceProductMinDisplay
        }),
        notLessThanOrEqualTo: getMessage('help-text__value-max', {
          prop: getMessage('price-product'), max: priceProductMaxDisplay
        })
      }
    },
    targetYield: {
      presence: {
        message: getMessage('help-text__value-required', { prop: getMessage('target-yield') })
      },
      numericality: {
        notValid: getMessage('help-text__value-num', { prop: getMessage('target-yield') }),
        greaterThanOrEqualTo: targetYieldMin,
        lessThanOrEqualTo: targetYieldMax,
        notGreaterThanOrEqualTo: getMessage('help-text__value-min', {
          prop: getMessage('target-yield'), min: formatNumberGrouped(targetYieldMin)
        }),
        notLessThanOrEqualTo: getMessage('help-text__value-max', {
          prop: getMessage('target-yield'), max: formatNumberGrouped(targetYieldMax)
        })
      }
    },
    currentAveragePopulation: {
      presence: {
        message: getMessage('help-text__value-required', { prop: getMessage('seeding-rate') })
      },
      numericality: {
        onlyInteger: true,
        notInteger: getMessage('value-needs-to-be-integer'),
        notValid: getMessage('help-text__value-num'),
        greaterThanOrEqualTo: rateMin,
        lessThanOrEqualTo: rateMax,
        notGreaterThanOrEqualTo: getMessage('help-text__value-min', {
          prop: getMessage('seeding-rate'), min: formatNumberGrouped(rateMin)
        }),
        notLessThanOrEqualTo: getMessage('help-text__value-max', {
          prop: getMessage('seeding-rate'), max: formatNumberGrouped(rateMax)
        })
      }
    },
    agronomicSeason: { presence: { allowEmpty: false } }
  };

  const rulesToTest = pick(rules, Object.keys(toTest));
  return validateWithOptions(toTest, rulesToTest);
};

export const validateAdvancedPlantingPlan = ({ 
  currency,
  plantingPlanParams,
  productValidationValues,
  cropId
}) => {
  // TODO: If these hyphenated things are coming from a service, clean them at parser level
  // ticket: SC-1709
  const {
    brand,
    'goal-ratio': goalRatio,
    productId,
    'price-commodity': priceCommodity,
    'price-product': priceProduct,
    'target-yield': targetYield,
    agronomicSeason,
    currentAveragePopulation
  } = plantingPlanParams;

  const {
    TARGET_YIELD: targetYieldConstraints,
    GRAIN_PRICE: priceCommodityConstraints,
    SEED_COST: priceProductConstraints,
    RATE: { max: rateMax }
  } = productValidationValues.PLANTING.CORN;

  const toTest = {
    brand,
    goalRatio,
    productId,
    priceCommodity,
    priceProduct,
    targetYield,
    agronomicSeason,
    currentAveragePopulation
  };

  const targetYieldConstrainsForDensityAdjustment = {
    ...targetYieldConstraints,
    min: Math.max(Math.ceil(targetYieldConstraints.max * RATIOS.MIN_TARGET_YIELD), MIN_TARGET_YIELD)
  };

  return validatePlantingPlan({
    toTest,
    constraints: {
      priceCommodity: {
        ...priceCommodityConstraints,
        min: Math.ceil(priceCommodityConstraints.max * RATIOS.MIN_GRAIN_PRICE)
      },
      priceProduct: priceProductConstraints,
      targetYield: targetYieldConstrainsForDensityAdjustment,
      currentAveragePopulation: {
        min: formatNumberRoundDownToNearest(rateMax * RATIOS.MIN_RATE, 1000),
        max: formatNumberRoundUpToNearest(rateMax, 1000)
      }
    },
    currency,
    cropId
  });
};

export const validateAdvancedPlantingPlanUi = ({
  currency,
  plantingPlanParams,
  productValidationValues,
}) => {
  // TODO: If these hyphenated things are coming from a service, clean them at parser level
  // ticket: SC-1709
  const {
    'price-commodity': priceCommodity,
    'price-product': priceProduct,
    'target-yield': targetYield,
    currentAveragePopulation
  } = plantingPlanParams;

  const {
    TARGET_YIELD: { max: targetYieldMax },
    GRAIN_PRICE: { max: priceCommodityMax },
    SEED_COST: { max: priceProductMax, min: priceProductMin },
    RATE: { max: rateMax }
  } = productValidationValues.PLANTING.CORN;

  const rateMin = formatNumberRoundDownToNearest(rateMax * RATIOS.MIN_RATE, 1000);
  const targetYieldMinForDensityAdjustmen = Math.max(
    Math.ceil(targetYieldMax * RATIOS.MIN_TARGET_YIELD),
    MIN_TARGET_YIELD
  );

  let rules = {
    productId: {
      presence: {
        message: getMessage('help-text__value-required', { prop: getMessage('price-commodity') })
      },
    },
    priceCommodity: {
      numericality: {
        notValid: getMessage('help-text__value-num', { prop: getMessage('price-commodity') }),
        greaterThanOrEqualTo: Math.ceil(priceCommodityMax * RATIOS.MIN_GRAIN_PRICE),
        lessThanOrEqualTo: priceCommodityMax,
        notGreaterThanOrEqualTo: getMessage('help-text__value-min', {
          prop: getMessage('price-commodity'), min: currencyFormatter(
            Math.ceil(priceCommodityMax * RATIOS.MIN_GRAIN_PRICE),
            currency
          )
        }),
        notLessThanOrEqualTo: getMessage('help-text__value-max', {
          prop: getMessage('price-commodity'), max: currencyFormatter(priceCommodityMax, currency)
        })
      }
    },
    priceProduct: {
      numericality: {
        notValid: getMessage('help-text__value-num', { prop: getMessage('price-product') }),
        greaterThanOrEqualTo: priceProductMin,
        lessThanOrEqualTo: priceProductMax,
        notGreaterThanOrEqualTo: getMessage('help-text__value-min', {
          prop: getMessage('price-product'), min: currencyFormatter(priceProductMin, currency)
        }),
        notLessThanOrEqualTo: getMessage('help-text__value-max', {
          prop: getMessage('price-product'), max: currencyFormatter(priceProductMax, currency)
        })
      }
    },
    targetYield: {
      numericality: {
        notValid: getMessage('help-text__value-num', { prop: getMessage('target-yield') }),
        greaterThanOrEqualTo: targetYieldMinForDensityAdjustmen,
        lessThanOrEqualTo: targetYieldMax,
        notGreaterThanOrEqualTo: getMessage('help-text__value-min', {
          prop: getMessage('target-yield'),
          min: formatNumberGrouped(targetYieldMinForDensityAdjustmen)
        }),
        notLessThanOrEqualTo: getMessage('help-text__value-max', {
          prop: getMessage('target-yield'), max: formatNumberGrouped(targetYieldMax)
        })
      }
    },
    currentAveragePopulation: {
      numericality: {
        onlyInteger: true,
        notInteger: getMessage('value-needs-to-be-integer'),
        notValid: getMessage('help-text__value-num'),
        greaterThanOrEqualTo: rateMin,
        lessThanOrEqualTo: formatNumberRoundUpToNearest(rateMax, 1000),
        notGreaterThanOrEqualTo: getMessage('help-text__value-min', {
          prop: getMessage('seeding-rate'), min: formatNumberGrouped(rateMin)
        }),
        notLessThanOrEqualTo: getMessage('help-text__value-max', {
          prop: getMessage('seeding-rate'), max: formatNumberGrouped(rateMax)
        })
      }
    }
  };
  // pick out the properties that have values assigned
  const toTest = pickBy({ priceCommodity, priceProduct, targetYield, currentAveragePopulation }, (value) => {
    return !isUndefined(value) && !isNull(value);
  });

  // pick out the rules that correspond to existing values
  const rulesToTest = pick(rules, Object.keys(toTest));
  return validateWithOptions(toTest, rulesToTest);
};

export const validateManualCropProtectionPlan = ({ getState, plantingPlanParams }) => {
  const { products } = plantingPlanParams;
  const state = getState();

  const { [applicationTypes.APPLICATION]: applicationProducts } = products;
  const currency = getCurrency(state);
  const cropId = getActiveCropId(state);

  let toTest = ['date', 'products', 'scriptName'].reduce((result, property) => {
    result[property] = get(plantingPlanParams, property);
    // validate empty string script name as being missing so
    // that the 'required' validation will show instead of the
    // format validation
    if (property === 'scriptName' && result[property] === '') {
      result[property] = null;
    }
    return result;
  }, {});
  toTest = {...toTest, products: applicationProducts ? Object.values(applicationProducts) : [] };

  return validatePlantingPlan({ toTest, currency, cropId });
};

export const validateManualFertilityPlan = ({ getState, plantingPlanParams }) => {
  const {
    products,
    scriptName,
    substanceType
  } = plantingPlanParams;

  const { [applicationTypes.FERTILIZER]: applicationProducts } = products;
  const state = getState();
  const currency = getCurrency(state);
  const cropId = getActiveCropId(state);

  let toTest = ['date','products','substanceType'];
  if (substanceTypeIsNitrogen(state, substanceType)) {
    toTest = [...toTest, 'incorporationMethod'];
  }
  if (isString(scriptName)) {
    toTest = [...toTest, 'scriptName'];
  }
  if (substanceTypeIsNitrogen(state, substanceType)) {
    toTest = [...toTest, 'incorporationMethod'];
  }
  toTest = toTest.reduce((result, property) => {
    result[property] = get(plantingPlanParams, property);
    // validate empty string script name as being missing so
    // that the 'required' validation will show instead of the
    // format validation
    if (property === 'scriptName' && result[property] === '') {
      result[property] = null;
    }
    return result;
  }, {});
  toTest = { ...toTest, products: applicationProducts ? Object.values(applicationProducts) : [] };

  return validatePlantingPlan({ toTest, currency, cropId });
};

export const validateManualPlantingPlan = ({ getState, plantingPlanParams, currency }) => {
  const {
    scriptName
  } = plantingPlanParams;

  const state = getState();
  const cropId = getActiveCropId(state);

  let toTest = ['date'];
  if (isString(scriptName)) {
    toTest = [...toTest, 'scriptName'];
  }
  toTest = toTest.reduce((result, property) => {
    result[property] = get(plantingPlanParams, property);
    // validate empty string script name as being missing so
    // that the 'required' validation will show instead of the
    // format validation
    if (property === 'scriptName' && result[property] === '') {
      result[property] = null;
    }
    return result;
  }, {});

  return validatePlantingPlan({ toTest, currency, cropId });
};

export const validateZoneApplicationProperty = ({
  cropId,
  format,
  productValidationValues,
  property,
  scriptType,
  substanceSet,
  value
}) => {
  const toTest = isNull(value) ? {} : { [property]: value }; // allow for null values
  const {
    TARGET_YIELD: { min: targetYieldMin, max: targetYieldMax },
    RATE: { min: rateMin, max: rateMax }
  } = determineProductValidationValueByCropId(productValidationValues, cropId);

  let fertilizerMax, fertilizerMin;

  if (scriptType === scriptTypes.FERTILITY) {
    fertilizerMax = get(
      productValidationValues,
      `FERTILITY.${substanceSet.toUpperCase()}.${format.toUpperCase()}.RATE.max`
    );
    fertilizerMin = get(
      productValidationValues,
      `FERTILITY.${substanceSet.toUpperCase()}.${format.toUpperCase()}.RATE.min`
    );
  } else if (scriptType === scriptTypes.APPLICATION) {
    fertilizerMax = get(
      productValidationValues,
      `APPLICATION.${substanceSet.toUpperCase()}.${format.toUpperCase()}.RATE.max`
    );
    fertilizerMin = get(
      productValidationValues,
      `APPLICATION.${substanceSet.toUpperCase()}.${format.toUpperCase()}.RATE.min`
    );
  } else {
    fertilizerMax = get(productValidationValues, 'FERTILITY.FERTILIZER.RATE.max');
    fertilizerMin = get(productValidationValues, 'FERTILITY.FERTILIZER.RATE.min');
  }
  const targetYieldString = getMessage('target-yield');
  const seedRateString = getMessage('rate');
  const fertilizerRateString = getMessage('fertilizer-rate');
  const fertilizerString = getMessage('liquid');

  const rules = {
    [scriptTypes.FERTILITY]: {
      [applicationProperties.FERTILIZER_RATE]: {
        numericality: {
          greaterThanOrEqualTo: fertilizerMin,
          lessThanOrEqualTo: fertilizerMax,
          notGreaterThanOrEqualTo: getMessage('help-text__value-min', {
            prop: fertilizerRateString, min: formatNumberGrouped(fertilizerMin)
          }),
          notLessThanOrEqualTo: getMessage('help-text__value-max', {
            prop: fertilizerRateString, max: formatNumberGrouped(fertilizerMax)
          })
        }
      },
      [applicationProperties.NITROGEN_RATE]: {
        numericality: {
          greaterThanOrEqualTo: fertilizerMin,
          lessThanOrEqualTo: fertilizerMax,
          notGreaterThanOrEqualTo: getMessage('help-text__value-min', {
            prop: fertilizerRateString, min: formatNumberGrouped(fertilizerMin)
          }),
          notLessThanOrEqualTo: getMessage('help-text__value-max', {
            prop: fertilizerRateString, max: formatNumberGrouped(fertilizerMax)
          })
        }
      }
    },
    [scriptTypes.PLANTING]: {
      [applicationProperties.TARGET_YIELD]: {
        numericality: {
          notValid: getMessage('help-text__value-num', { prop: getMessage('target-yield') }),
          greaterThanOrEqualTo: targetYieldMin,
          lessThanOrEqualTo: targetYieldMax,
          notGreaterThanOrEqualTo: getMessage('help-text__value-min', {
            prop: targetYieldString, min: formatNumberGrouped(targetYieldMin)
          }),
          notLessThanOrEqualTo: getMessage('help-text__value-max', {
            prop: targetYieldString, max: targetYieldMax
          })
        }
      },
      [applicationProperties.SEED_RATE]: {
        presence: { message: getMessage('help-text__value-required', { prop: seedRateString }) },
        numericality: {
          notValid: getMessage('help-text__value-required', { prop: seedRateString }),
          greaterThanOrEqualTo: rateMin,
          lessThanOrEqualTo: rateMax,
          notGreaterThanOrEqualTo: getMessage('help-text__value-min', {
            prop: seedRateString, min: formatNumberGrouped(rateMin)
          }),
          notLessThanOrEqualTo: getMessage('help-text__value-max', {
            prop: seedRateString, max: formatNumberGrouped(rateMax)
          })
        }
      },
      [applicationProperties.FERTILIZER_RATE]: {
        numericality: {
          greaterThanOrEqualTo: fertilizerMin,
          lessThanOrEqualTo: fertilizerMax,
          notGreaterThanOrEqualTo: getMessage('help-text__value-min', {
            prop: fertilizerString, min: formatNumberGrouped(fertilizerMin)
          }),
          notLessThanOrEqualTo: getMessage('help-text__value-max', {
            prop: fertilizerString, max: formatNumberGrouped(fertilizerMax)
          })
        }
      }
    },
    [scriptTypes.APPLICATION]: {
      [applicationProperties.FERTILIZER_RATE]: {
        numericality: {
          greaterThanOrEqualTo: fertilizerMin,
          lessThanOrEqualTo: fertilizerMax,
          notGreaterThanOrEqualTo: getMessage('help-text__value-min', {
            prop: fertilizerRateString, min: formatNumberGrouped(fertilizerMin)
          }),
          notLessThanOrEqualTo: getMessage('help-text__value-max', {
            prop: fertilizerRateString, max: formatNumberGrouped(fertilizerMax)
          })
        }
      },
      rate: {
        numericality: {
          greaterThanOrEqualTo: fertilizerMin,
          lessThanOrEqualTo: fertilizerMax,
          notGreaterThanOrEqualTo: getMessage('help-text__value-min', {
            prop: fertilizerRateString, min: formatNumberGrouped(fertilizerMin)
          }),
          notLessThanOrEqualTo: getMessage('help-text__value-max', {
            prop: fertilizerRateString, max: formatNumberGrouped(fertilizerMax)
          })
        }
      },
      [applicationProperties.APPLICATION]: {
        numericality: {
          greaterThanOrEqualTo: fertilizerMin,
          lessThanOrEqualTo: fertilizerMax,
          notGreaterThanOrEqualTo: getMessage('help-text__value-min', {
            prop: fertilizerRateString, min: formatNumberGrouped(fertilizerMin)
          }),
          notLessThanOrEqualTo: getMessage('help-text__value-max', {
            prop: fertilizerRateString, max: formatNumberGrouped(fertilizerMax)
          })
        }
      }
    }
  };


  return validateWithOptions(toTest, pick(rules[scriptType], [property]));
};

export const unitTestPrivate = {
  validateWithOptions
};
